import { Injectable } from '@angular/core';
import { ComponentController, filterNil } from '@lycoris-nubila/ngx-lycoris';
import { TranslateService } from '@ngx-translate/core';
import { Observable, from } from 'rxjs';
import {
	finalize,
	map,
	pluck,
	shareReplay,
	startWith,
	switchMap,
} from 'rxjs/operators';

import { environment } from 'environments/environment';
import {
	PageWithHeaderState,
	PageWithHeaderStore,
} from 'presentation/common/component/page-with-header/page-with-header.store';
import { UserApplicationPort } from 'user/port/user-application.port';
import { UserComputedRemark } from 'user/user-computed-remark';

@Injectable()
export class PageWithHeaderController extends ComponentController<PageWithHeaderState> {
	public i18n = 'presentation.common.component.page-with-header';

	public title$: Observable<string>;
	public pictureUrl$: Observable<string>;
	public headerImageUrl$: Observable<string>;
	public remark$: Observable<UserComputedRemark>;
	public isUploadingAvatar$: Observable<boolean>;

	constructor(
		store: PageWithHeaderStore,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort
	) {
		super(store);

		this._initObservables();
	}

	private _initObservables() {
		this.headerImageUrl$ = this._userApplicationPort.observeSessionUser().pipe(
			filterNil(),
			pluck('organizationId'),
			map((organizationId) =>
				environment.headerImageUrl.replace('{id}', organizationId)
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.pictureUrl$ = this._userApplicationPort.observeSessionUser().pipe(
			map((user) => user?.pictureUrl ?? environment.headerPicturePlaceholder),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.title$ = this._userApplicationPort.observeSessionUser().pipe(
			switchMap((user) =>
				this._translateService.stream(this.i18n + '.titleElement.text', {
					firstName: user?.firstName,
				})
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.remark$ = this._userApplicationPort.observeSessionUser().pipe(
			filterNil(),
			switchMap(() => this._userApplicationPort.fetchStatisticsUser()),
			filterNil(),
			pluck('computedRemark'),
			filterNil(),
			startWith('DEFAULT'),
			switchMap((remark) =>
				this._translateService.stream(this.i18n + `.remarkElement.${remark}`)
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.isUploadingAvatar$ = this.select('isUploadingAvatar').pipe(
			shareReplay({ refCount: true, bufferSize: 1 })
		);
	}

	public updateUserAvatar(file: any) {
		this.store.update({ isUploadingAvatar: true });

		return from(this._userApplicationPort.updateUserAvatarUrl(file))
			.pipe(finalize(() => this.store.update({ isUploadingAvatar: false })))
			.toPromise();
	}
}
